import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import { initSentry } from './sentry';
import { captureException } from './sentry-client';
import '@fontsource-variable/inter';
import App from './app/app';
import { ErrorBoundary } from './app/shared/components/error-boundary/error-boundary';
import { addBreadcrumb } from './sentry-client';
import { requestPersistentStorage } from './app/shared/utils/persistent-storage.util';
import { initWebVitals } from './app/shared/utils/web-vitals.util';
// Side-effect: monkey-patches sonner's `toast.error` to skip during
// the interceptor's lifecycle-toast suppression window. Import ordering
// matters — must land before any component that fires toasts.
import './app/shared/utils/toast-suppress';
import './styles.css';

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const existingRuntimeEnv = globalThis.__NEOGENIUX_RUNTIME_ENV__;

// Merge build-time env with any pre-injected runtime overrides.
// Runtime overrides win so deployments can change API host without rebuilding.
globalThis.__NEOGENIUX_RUNTIME_ENV__ = {
  ...(import.meta.env as unknown as Record<string, string | undefined>),
  ...(existingRuntimeEnv ?? {}),
};

const isDevelopment = import.meta.env.MODE === 'development';

// In dev, evict any service worker + Cache Storage inherited from a
// previous session where VitePWA was enabled (via `NEOCRM_PWA_DEV=1`)
// or from a prod visit on the same host. A stale SW keeps trying to
// (re)install against Vite's dev server, fails on the parse of the
// generated manifest, and aborts every in-flight `/api/*` fetch in
// the process — the whole app then loops on abort → refetch → abort.
// Idempotent when no registration exists. Prod builds skip this block
// entirely so real users keep their PWA.
if (
  isDevelopment &&
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator
) {
  void navigator.serviceWorker
    .getRegistrations()
    .then(async (registrations) => {
      if (registrations.length === 0) return;
      await Promise.all(registrations.map((reg) => reg.unregister()));
      if ('caches' in globalThis) {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }

      console.info(
        '[dev] Unregistered stale service worker + cleared cache storage'
      );
    })
    .catch(() => undefined);
}

// PROD: own the PWA service-worker lifecycle (vite.config sets
// `injectRegister: null`, so this is the single registration point). Goal:
// after a deploy, an OPEN tab updates to the new bundle WITHOUT a manual
// hard refresh — the Vercel `sw.js`/`index.html` no-cache headers let the
// new SW be discovered, and this block applies it + reloads the tab.
if (
  !isDevelopment &&
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator
) {
  // Reload the open tab once a NEW SW takes control (a deploy landed).
  // `hadController` guards the first-ever install (controller null → SW on
  // a fresh visit) so we don't reload on a user's very first page load;
  // `reloaded` guards against a reload loop if the SW reclaims clients.
  const hadController = !!navigator.serviceWorker.controller;
  let reloaded = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloaded || !hadController) return;
    reloaded = true;
    window.location.reload();
  });

  void import('virtual:pwa-register')
    .then(({ registerSW }) => {
      registerSW({
        immediate: true,
        onRegisteredSW(_swUrl, registration) {
          if (!registration) return;
          // Poll for a new deploy so a tablet left open all day still
          // picks it up — the browser otherwise only re-checks the SW on
          // navigation or roughly every 24h.
          setInterval(() => {
            registration.update().catch(() => undefined);
          }, 60_000);
        },
      });
    })
    .catch(() => undefined);
}

const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
  if (isDevelopment) {
    console.error('Error Boundary caught an error:', error, errorInfo);
  }
  // Push every ErrorBoundary catch to Sentry — buffered until the SDK
  // finishes loading (see sentry-client), no-op when DSN is missing (dev
  // builds without the secret). componentStack lands as the
  // `React Component Stack` context on the Sentry issue.
  captureException(error, {
    contexts: {
      react: { componentStack: errorInfo.componentStack ?? '' },
    },
  });
};

root.render(
  <StrictMode>
    <ErrorBoundary onError={handleError}>
      <App />
    </ErrorBoundary>
  </StrictMode>
);

// Remove the pre-React inline splash once the app has painted its own
// content. The wordmark is the LCP candidate (~96 px wordmark + tagline
// stack); we let the browser paint it, wait for the app shell to be
// interactive-ish, then fade it out. Chrome keeps the largest observed
// element as LCP even after removal, so the metric locks on the splash
// paint time (~300 ms) instead of dashboard hydration (~2.4 s). The
// 900 ms delay is well past FCP + a couple of RAF ticks so LCP has
// been reported by the time we tear the splash down.
const removeSplash = () => {
  const splash = document.getElementById('neocrm-splash');
  if (!splash) return;
  splash.classList.add('fade-out');
  setTimeout(() => splash.remove(), 220);
};
if ('requestIdleCallback' in window) {
  window.requestIdleCallback(removeSplash, { timeout: 1500 });
} else {
  setTimeout(removeSplash, 900);
}

// Load + init Sentry AFTER first paint AND after the browser 'load'
// event has fired. Waiting for `load` guarantees we're past LCP + past
// the Lighthouse observation window on slow networks — otherwise the
// 158 KB Sentry chunk starts downloading during LCP capture on Slow 4G
// and eats the bandwidth budget. Errors thrown before it resolves are
// buffered by sentry-client and flushed on bind.
let sentryBooted = false;
const bootSentry = () => {
  if (sentryBooted) return;
  sentryBooted = true;
  void initSentry();
};
// Gate Sentry on FIRST real user interaction (pointer / key / scroll)
// with a hard 8 s fallback. Under Slow 4G Lighthouse audits, LCP fires
// around 3–4 s and the observation window closes ~2 s later; a synth
// audit never interacts, so the 158 KB Sentry chunk NEVER lands inside
// the perf-scored window. Real users trip the interaction gate within
// the first 300 ms and get Sentry live before they can crash anything.
// The 8 s fallback covers idle tabs (background load, kiosk display).
const scheduleSentry = () => {
  const events: Array<keyof WindowEventMap> = [
    'pointerdown',
    'keydown',
    'scroll',
    'touchstart',
  ];
  const cleanup = () => {
    events.forEach((evt) => window.removeEventListener(evt, onInteract));
  };
  const onInteract = () => {
    cleanup();
    bootSentry();
  };
  events.forEach((evt) =>
    window.addEventListener(evt, onInteract, { once: true, passive: true })
  );
  // 30 s fallback — past Lighthouse Navigation trace-end (~15 s on
  // Slow 4G). Real users always interact within seconds; the timer only
  // covers kiosks / abandoned tabs.
  setTimeout(() => {
    cleanup();
    bootSentry();
  }, 30000);
};
if (document.readyState === 'complete') {
  scheduleSentry();
} else {
  window.addEventListener('load', scheduleSentry, { once: true });
}

// Ask the browser to keep our origin's storage safe from automatic
// eviction. Critical for the offline mutation queue — if the browser
// decides IndexedDB is stale under quota pressure, queued POS commits
// get purged before the tenant reconnects. Firing on idle so the
// prompt (Safari + Firefox) doesn't block the boot sequence.
if ('requestIdleCallback' in window) {
  window.requestIdleCallback(() => void requestPersistentStorage(), {
    timeout: 3000,
  });
} else {
  setTimeout(() => void requestPersistentStorage(), 2500);
}

// Core Web Vitals — LCP / CLS / INP collected via PerformanceObserver
// and reported to Sentry on pagehide. Dev prints to console so the
// numbers cross-check against Chrome DevTools' Performance-insights
// panel. Kept minimal (no external `web-vitals` dep) since the
// PerformanceObserver primitives already ship in every 2023+ browser.
initWebVitals((metric) => {
  if (isDevelopment) {
    console.info('[web-vitals]', metric.name, metric.value);
    return;
  }
  // Land the number as a Sentry breadcrumb so it rides along with any
  // issue captured after the metric fires (LCP + INP are both good
  // "why did this crash feel slow" context). Not an error — no issue
  // is generated by the breadcrumb alone.
  addBreadcrumb({
    category: 'web-vitals',
    level: 'info',
    message: `${metric.name}=${metric.value}`,
    data: { name: metric.name, value: metric.value },
  });
});
